import { useState } from 'react';
import kit from './kit.module.css';
import styles from './ExpectationSettingDemo.module.css';

function Mark({ ok }: { ok: boolean }) {
  return (
    <span className={`${kit.chip} ${ok ? kit.chipGood : kit.chipWarn} ${styles.mark}`} aria-hidden="true">
      {ok ? '✓' : '!'}
    </span>
  );
}

// ── First-time onboarding scenario ────────────────────────────────────────────

const EXPECTATIONS = [
  { ok: true, text: 'Generates a complete, structured CV in under 30 seconds' },
  { ok: true, text: 'Handles employment gaps with neutral, professional language' },
  { ok: false, text: 'Always review the personal statement - it may need your personal touch' },
  { ok: false, text: 'Check dates and job titles before downloading' },
];

function FirstTimeScenario() {
  const [seen, setSeen] = useState(false);

  if (seen) {
    return (
      <>
        <div className={kit.agent}>
          <span className={kit.agentIcon} aria-hidden="true">✦</span>
          <p>You're ready to create your first CV. What role are you applying for?</p>
        </div>
        <p className={kit.feedback} aria-live="polite">
          The onboarding message has been dismissed and won't show again.
        </p>
        <div>
          <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={() => setSeen(false)}>
            Show onboarding again
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>Before you start</span>
          <span>CV builder</span>
        </div>
        <h3 className={kit.proposalTitle}>Here's what to expect from this tool</h3>
        <ul className={styles.list}>
          {EXPECTATIONS.map((item) => (
            <li key={item.text} className={styles.item}>
              <Mark ok={item.ok} />
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
        <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setSeen(true)}>
          Got it, let's go
        </button>
      </div>
      <p className={kit.feedback}>This message won't appear again.</p>
    </>
  );
}

// ── Return user scenario ──────────────────────────────────────────────────────

type ReturnPhase = 'banner' | 'expanded' | 'dismissed';

const AI_QUALITY = [
  { label: 'Structure and formatting', note: 'reliable', ok: true },
  { label: 'Employment history', note: 'based on what you entered', ok: true },
  { label: 'Personal statement', note: 'review carefully', ok: false },
  { label: 'Dates', note: 'verify against original documents', ok: false },
];

function ReturnUserScenario() {
  const [phase, setPhase] = useState<ReturnPhase>('banner');

  return (
    <>
      {phase !== 'dismissed' && (
        <div className={styles.banner}>
          <div className={styles.bannerTop}>
            <span className={`${kit.chip} ${kit.chipWarn}`}>AI-assisted</span>
            <span className={styles.bannerText}>Always review before sending</span>
            {phase === 'banner' && (
              <button
                type="button"
                className={`${kit.btn} ${kit.btnGhost} ${styles.more}`}
                onClick={() => setPhase('expanded')}
                aria-label="Show AI quality details"
              >
                What to check
              </button>
            )}
          </div>

          {phase === 'expanded' && (
            <div className={styles.detail}>
              <p className={kit.feedback}>This CV was generated by your AI assistant.</p>
              <ul className={styles.list}>
                {AI_QUALITY.map((item) => (
                  <li key={item.label} className={styles.item}>
                    <Mark ok={item.ok} />
                    <span>
                      <strong>{item.label}:</strong> {item.note}
                    </span>
                  </li>
                ))}
              </ul>
              <div>
                <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setPhase('dismissed')}>
                  Got it
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>CV draft</span>
          <span>Alex Johnson</span>
        </div>
        <h3 className={kit.proposalTitle}>Senior Retail Associate</h3>
        <dl className={`${kit.fields} ${styles.fields}`}>
          <dt>Employment</dt>
          <dd>January 2019 - March 2021 · Managed a team of 4 during peak periods</dd>
          <dt>Statement</dt>
          <dd>A motivated professional with experience in customer-facing roles...</dd>
        </dl>
      </div>

      {phase === 'dismissed' && (
        <div className={kit.row}>
          <p className={kit.feedback} aria-live="polite">AI quality indicator dismissed.</p>
          <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={() => setPhase('banner')}>
            Show again
          </button>
        </div>
      )}
    </>
  );
}

// ── Top-level component with tabs ─────────────────────────────────────────────

type Tab = 'first-time' | 'return-user';

const TABS: { id: Tab; label: string }[] = [
  { id: 'first-time', label: 'First time' },
  { id: 'return-user', label: 'Return user' },
];

export function ExpectationSettingDemo() {
  const [tab, setTab] = useState<Tab>('first-time');

  return (
    <div className={kit.root}>
      <div className={styles.tabs} role="tablist" aria-label="Scenario">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div key={tab} className={kit.stack}>
        {tab === 'first-time' ? <FirstTimeScenario /> : <ReturnUserScenario />}
      </div>
    </div>
  );
}
